import type { InjectionKey } from 'vue';
import type { Maybe } from '@/helpers/types';
import type { MaybeRefOrGetter } from '@vueuse/core';
import type { TrackingSpecs } from '@/libraries/tracking/specTracker';
import type { HomeModule } from '@/features/onboarded-home/types';

import { SnowplowModuleContext } from '@/helpers/tracking/providers';

import { createSpecTracker, defineSpec } from '@/libraries/tracking/specTracker';

import { toValue } from '@vueuse/core';
import { computed, inject, provide } from 'vue';

const specs = {
	impression: {
		category: 'module',
		action: 'impression',
		nonInteraction: true,
		triggerCheck: (isVisible: boolean) => isVisible,
	},

	titleClicked: defineSpec<{
		/** Module name. */
		label: string;
	}>()({
		category: 'userinteraction',
		action: 'title_clicked',
		property: 'home',
	}),

	// SponsoredRecommendation
	// blank event to trigger session start callback
	moreInfoClick: {
		triggerCheck: () => false,
	},

	// NewOnYourServicesModule
	// max@todo should this event start a session?
	newOnYourServiceClick: defineSpec<{
		label: 'more-card' | 'package-icon';
		// max@todo could this be the short name?
		/** packageId as string */
		property: string;
	}>()({
		category: 'userinteraction',
		action: 'click',
	}),

	// EditorsPickModule
	editorialHeaderClick: defineSpec<{
		label: string;
	}>()({
		category: 'userinteraction',
		action: 'content_clicked',
		property: 'editorial_pick',
		useLastRoute: true,
	}),

	authorClick: {
		triggerCheck: () => false,
	},

	// TextRecommendationModule
	wtwClick: defineSpec<{
		value?: number;
	}>()({
		category: 'why_to_watch',
		action: 'click',
	}),

	// SportsModule
	// blank event to trigger session start callback
	navigateToSports: {
		triggerCheck: () => false,
	},

	hideSportsModule: {
		category: 'userinteraction',
		action: 'dismiss_clicked',
	},
} satisfies TrackingSpecs;

/** Click or other module interactions that kick off an influenced session. */
//const SESSION_START_EVENTS: Array<keyof typeof specs> = [
//	'titleClicked',
//	'moreInfoClick',
//	'editorialHeaderClick',
//	'authorClick',
//	'navigateToSports',
//];

//const HomeSessionId = Symbol('HomeModuleSession');

//interface SessionStart {
//	name: string;
//	path: string;
//}

interface OnboardedHomeTrackingOptions {
	moduleData: MaybeRefOrGetter<NonNullable<HomeModule>>;

	/** Global row index position on the page. */
	globalPosition: MaybeRefOrGetter<number>;

	/** Local horizontal index within the row. */
	localPosition?: MaybeRefOrGetter<Maybe<number>>;

	/** Total number of titles in a module showing multiple titles. */
	titleSize?: MaybeRefOrGetter<Maybe<number>>;

	/**
	 * ID of the entity that we are using to show the module content.
	 * List ID, Article ID, Person ID, whatever that can help to link to content object behind...
	 */
	objectId?: MaybeRefOrGetter<string | null>;
}

export function useHomeModuleTracking({
	moduleData,
	globalPosition,
	localPosition,
	objectId,
	titleSize,
}: OnboardedHomeTrackingOptions) {
	/* SESSION */
	//const { session, attachSession, killSession, stopInterval } = createSessionManager<
	//	SnowplowModuleContext,
	//	SessionStart
	//>({ id: HomeSessionId, influenced: true });

	/* TRACKING */
	const { injectGlobalModuleSize, injectLocalModuleSize, injectModuleContainerType } = useModuleContextData();
	const moduleContext = computed(
		() =>
			new SnowplowModuleContext(toValue(moduleData), {
				objectId: toValue(objectId),
				globalPosition: toValue(globalPosition),
				localPosition: toValue(localPosition),
				globalSize: injectGlobalModuleSize(),
				localSize: injectLocalModuleSize(),
				titleSize: toValue(titleSize),
				containerType: injectModuleContainerType(),
			})
	);

	const trackHomeModule = createSpecTracker({ specs, baseContexts: () => [moduleContext.value] });

	const moduleObserveVisibility = {
		once: true,
		active: false,
		throttle: 300,
		// 60% visibility of each element
		intersection: { root: null, rootMargin: '0px 0px 0px 0px', threshold: 0.6 },
		callback: (isVisible: boolean) => trackHomeModule('impression', { checkArgs: isVisible }),
	};

	/* SESSION MANAGEMENT */
	//const route = useRoute();

	// Start module session
	//function startNewSession() {
	//	if (moduleContext.value == null) return;
	//	attachSession({ name: route.name ?? '', path: route.path }, moduleContext.value);
	//}

	//SESSION_START_EVENTS.forEach(event => trackHomeModule.on(event, startNewSession));

	// End module session
	//const router = useRouter();
	//router.beforeEach((to, from, next) => {
	//	if (to.name == null || from.name == null) {
	//		killSession();
	//		next();
	//		return;
	//	}
	//
	//	const currentSession = session();
	//
	//	// prettier-ignore
	//	const shouldKillSession =
	//		// there's a session to kill
	//		currentSession !== null &&
	//		// coming back to home page
	//		to.name === 'app.home' && from.name !== 'app.home';
	//
	//	if (shouldKillSession) killSession();
	//
	//	next();
	//});
	//
	//tryOnScopeDispose(() => {
	//	stopInterval();
	//	SESSION_START_EVENTS.forEach(event => trackHomeModule.off(event, startNewSession));
	//});

	return {
		moduleContext,
		trackHomeModule,
		moduleObserveVisibility,
	};
}

const MODULE_CONTAINER_TYPE_KEY = Symbol('Module Container Type') as InjectionKey<string>;
const GLOBAL_MODULE_SIZE_KEY = Symbol('Global Module Size') as InjectionKey<number>;
const LOCAL_MODULE_SIZE_KEY = Symbol('Local Module Size') as InjectionKey<number | null>;

/** Helper to provide and inject various tracking context only properties. */
export function useModuleContextData() {
	/** The top level number of modules. */
	function provideGlobalModuleSize(size: MaybeRefOrGetter<number>) {
		provide(GLOBAL_MODULE_SIZE_KEY, toValue(size));
	}

	/** The top level number of modules. */
	function injectGlobalModuleSize() {
		return inject<number>(GLOBAL_MODULE_SIZE_KEY, 0);
	}

	/** The number of modules inside a module container (Spotlight, Rotator, ...). */
	function provideLocalModuleSize(size: MaybeRefOrGetter<number>) {
		provide(LOCAL_MODULE_SIZE_KEY, toValue(size));
	}

	/** The number of modules inside a module container (Spotlight, Rotator, ...). */
	function injectLocalModuleSize() {
		return inject<number | null>(LOCAL_MODULE_SIZE_KEY, null);
	}

	/** The module type of a module container (Spotlight, Rotator, ...). */
	function provideModuleContainerType(containerType: MaybeRefOrGetter<string>) {
		provide(MODULE_CONTAINER_TYPE_KEY, toValue(containerType));
	}

	/** The module type of a module container (Spotlight, Rotator, ...). */
	function injectModuleContainerType() {
		return inject<string | null>(MODULE_CONTAINER_TYPE_KEY, null);
	}

	return {
		provideGlobalModuleSize,
		injectGlobalModuleSize,

		provideLocalModuleSize,
		injectLocalModuleSize,

		provideModuleContainerType,
		injectModuleContainerType,
	};
}
